/**
 * ESM button loading helpers (Material `sync` + animate-spin).
 * Prefer these from ESM modules; classic pages may still use window.safeSetButtonLoading.
 */

const SPINNER_MARKUP = (loadingLabel) =>
  `<span class="inline-flex items-center justify-center gap-2"><span class="material-icons animate-spin text-[18px] leading-none">sync</span><span>${loadingLabel}</span></span>`;

/**
 * Toggle a button into a loading / idle visual state.
 * Preserves original HTML for restore.
 *
 * @param {HTMLButtonElement|HTMLElement|null|undefined} button
 * @param {boolean} isLoading
 * @param {string} [loadingLabel]
 */
export function setButtonLoading(button, isLoading, loadingLabel = "Aguarde...") {
  if (!button) return;

  if (isLoading) {
    if (button.dataset.originalHtml === undefined) {
      button.dataset.originalHtml = button.innerHTML;
    }
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    button.innerHTML = SPINNER_MARKUP(loadingLabel);
    button.classList.add("opacity-70", "cursor-wait");
    return;
  }

  button.disabled = false;
  button.removeAttribute("aria-busy");
  button.classList.remove("opacity-70", "cursor-wait");
  if (button.dataset.originalHtml !== undefined) {
    button.innerHTML = button.dataset.originalHtml;
    delete button.dataset.originalHtml;
  }
}

/**
 * Runs an async action with button spinner + aria-busy; restores in finally.
 * Ignores re-entry while the button is already loading.
 *
 * @param {HTMLButtonElement|HTMLElement|null|undefined} button
 * @param {string} loadingLabel
 * @param {() => Promise<unknown>} run
 * @param {{ siblings?: Array<HTMLElement|null|undefined> }} [options]
 * @returns {Promise<unknown>}
 */
export async function withButtonLoading(button, loadingLabel, run, options = {}) {
  if (typeof run !== "function") return undefined;
  if (!button) return run();
  if (button.dataset.loading === "true" || button.getAttribute("aria-busy") === "true") {
    return undefined;
  }

  const siblings = Array.isArray(options.siblings) ? options.siblings.filter(Boolean) : [];
  const controls = [...new Set([button, ...siblings])];
  const previouslyDisabled = new Map(controls.map((el) => [el, el.disabled]));

  button.dataset.loading = "true";
  controls.forEach((el) => {
    el.disabled = true;
  });
  setButtonLoading(button, true, loadingLabel);

  try {
    return await run();
  } finally {
    setButtonLoading(button, false);
    delete button.dataset.loading;
    controls.forEach((el) => {
      if (el === button) return;
      el.disabled = Boolean(previouslyDisabled.get(el));
    });
  }
}
